import React, { useState, useEffect } from 'react';
import { useSplash } from '../../../contexts/SplashContext';
import TranslatableInput from '../../Common/TranslatableInput';
import TranslatableTextarea from '../../Common/TranslatableTextarea';
import LanguageSwitch from '../../Common/LanguageSwitch';
import UserSelection from '../../Common/UserSelection';

const PushNotificationForm = ({ initialData, onSave, isLoading, onClose }) => {
    const splashData = useSplash();
    const languages = splashData?.languages || [];
    const defaultLanguage = languages.find(lang => lang.default)?.code || languages[0]?.code || 'en';
    const [selectedLanguage, setSelectedLanguage] = useState(defaultLanguage);

    const [formData, setFormData] = useState({
        title: {},
        message: {},
        user_type: '',
        user_ids: [],
        is_scheduled: false,
        scheduled_at: null,
        editable: true,
    });

    const [errors, setErrors] = useState({});
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (initialData) {
            setFormData({
                ...initialData,
                title: initialData.title || {},
                message: initialData.message || {},
                scheduled_at: initialData.scheduled_at ? new Date(initialData.scheduled_at).toISOString().slice(0, 16) : null,
                user_ids: initialData.user_ids || [],
                editable: initialData.editable !== undefined ? initialData.editable : true
            });
        }
    }, [initialData]);

    const handleChange = (e) => {
        if (!formData.editable) return;
        
        const { name, value, type, checked } = e.target;
        
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));

        if (errors[name]) {
            setErrors(prev => ({...prev, [name]: ''}));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.editable) return;
        
        try {
            setIsSaving(true);
            await onSave(formData);
            setErrors({});
            onClose();
        } catch (error) {
            if (error.errors) {
                const newErrors = {};
                Object.keys(error.errors).forEach(key => {
                    newErrors[key] = Array.isArray(error.errors[key]) 
                        ? error.errors[key][0] 
                        : error.errors[key];
                });
                console.log(newErrors);
                setErrors(newErrors);
            } else {
                setErrors({
                    name: error.message || 'An error occurred while saving'
                });
            }
            return;
        } finally {
            setIsSaving(false);
        }
    };

    const handleUserTypeChange = (selectedOption) => {
        if (!formData.editable) return;
        
        setFormData(prev => ({
            ...prev,
            user_type: selectedOption?.value || '',
            user_ids: [] // Clear selected users when user type changes
        }));
    };

    const handleUsersChange = (selectedOptions) => {
        if (!formData.editable) return;
        
        console.log(selectedOptions);
        setFormData(prev => ({
            ...prev,
            user_ids: selectedOptions
        }));
    };

    const isSubmitting = isLoading || isSaving;
    const selectedLang = languages.find(l => l.code === selectedLanguage);
    const isDisabled = isSubmitting || !formData.editable;

    return (
        <form onSubmit={handleSubmit} className="h-100 d-flex flex-column">
            <div className="d-flex justify-content-end mb-3">
                <LanguageSwitch onLanguageChange={setSelectedLanguage} />
            </div>
            <div className="flex-grow-1">
                <TranslatableInput
                    label="PushNotification Title"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    errors={errors}
                    required={true}
                    disabled={isDisabled}
                    selectedLanguage={selectedLanguage}
                    selectedLang={selectedLang}
                />
                
                <TranslatableTextarea
                    label="PushNotification Message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    errors={errors}
                    disabled={isDisabled}
                    selectedLanguage={selectedLanguage}
                    selectedLang={selectedLang}
                />

                <UserSelection
                    selectedUserType={formData.user_type ? { value: formData.user_type, label: formData.user_type === 'user' ? 'Users' : 'Service Providers' } : null}
                    selectedUsers={formData.user_ids}
                    onUserTypeChange={handleUserTypeChange}
                    onUsersChange={handleUsersChange}
                    isSubmitting={isDisabled}
                    errors={errors}
                    isMulti={true}
                    placeholder="Select recipients..."
                />

                <div className="d-flex align-items-center">
                    <div className="d-flex align-items-center">
                        <label className="form-check-label m-0 me-2" htmlFor="scheduled_notification">Schedule Notification?</label>
                        <div className="form-check form-switch form-switch-md m-0">
                            <input 
                                className="form-check-input" 
                                type="checkbox" 
                                id="scheduled_notification" 
                                name="scheduled_notification"
                                checked={formData.is_scheduled}
                                onChange={(e) => {
                                    if (!formData.editable) return;
                                    setFormData(prev => ({
                                        ...prev,
                                        is_scheduled: e.target.checked,
                                        scheduled_at: e.target.checked ? prev.scheduled_at : null
                                    }));
                                }}
                                disabled={isDisabled}
                            />
                        </div>
                    </div>
                </div>

                {formData.is_scheduled && (
                    <div className="my-3">
                        <label className="form-label">
                            Schedule
                        </label>
                        <input
                            type="datetime-local"
                            value={formData.scheduled_at || ''}
                            onChange={(e) => {
                                if (!formData.editable) return;
                                setFormData(prev => ({ ...prev, scheduled_at: e.target.value }));
                            }}
                            className={`form-control ${errors.scheduled_at ? 'is-invalid' : ''}`}
                            disabled={isDisabled}
                        />
                        {errors.scheduled_at && (
                            <div className="invalid-feedback">
                                {Array.isArray(errors.scheduled_at) ? errors.scheduled_at[0] : errors.scheduled_at}
                            </div>
                        )}
                    </div>
                )}
            </div>

            <div className="form-footer">
                <button 
                    type="button" 
                    className="btn btn-light" 
                    onClick={onClose}
                    disabled={isDisabled}
                >
                    Cancel
                </button>
                <button 
                    type="submit" 
                    className="btn btn-primary" 
                    disabled={isDisabled}
                >
                    {isSubmitting ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" />
                            Saving...
                        </>
                    ) : 'Save'}
                </button>
            </div>
        </form>
    );
};

export default PushNotificationForm;